import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Mail, MessageCircle, Printer } from 'lucide-react';
import { Button, Dialog, Field, Input, Spinner, useToast } from '@shripos/ui';
import { ReceiptView } from '../../components/ReceiptView.jsx';
import { api } from '../../lib/api.js';
import { usePrinting } from '../../lib/printing/usePrinting.js';
import { rupees } from '../../lib/money.js';

export function waLink(bill) {
  const text = `Thank you for visiting ${bill.snapshot?.business?.name ?? ''}! Bill ${bill.invoiceNo} · ${rupees(bill.totals.grandTotalPaise)}\nYour receipt: ${bill.receiptUrl}`;
  const phone = bill.customer?.phone?.replace(/\D/g, '');
  const to = phone ? (phone.length === 10 ? `91${phone}` : phone) : '';
  return `https://wa.me/${to}?text=${encodeURIComponent(text)}`;
}

export function EmailReceipt({ bill }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button variant="secondary" icon={Mail} onClick={() => setOpen(true)} disabled={!bill.receiptUrl}>
        {t('pos.email', 'Email')}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('pos.emailReceipt', 'Email receipt')}
        size="sm"
        footer={
          <Button
            loading={busy}
            disabled={!/^\S+@\S+\.\S+$/.test(to)}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/bills/${bill.id}/email`, { to });
                toast.success(t('pos.receiptEmailed', 'Receipt emailed to {{to}}', { to }));
                setOpen(false);
              } catch (err) {
                toast.error(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('pos.send', 'Send')}
          </Button>
        }
      >
        <Field label={t('pos.emailAddress', 'Email address')}>
          <Input type="email" autoFocus value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </Dialog>
    </>
  );
}

/**
 * After a sale: receipt preview + print / WhatsApp / email + start the next order.
 * autoPrinted: the receipt already went to the thermal printer (Settings → Receipt & printing).
 */
/** After a sale. Offline sales pass their receipt (built on this device) and sync later. */
export function SaleDoneDialog({ bill, kots, autoPrinted = false, onNext, offlineReceipt = null }) {
  const { t } = useTranslation();
  const receipt = useQuery({
    queryKey: ['receipt', bill.id],
    queryFn: () => api.get(`/bills/${bill.id}/receipt`),
    enabled: !offlineReceipt,
    initialData: offlineReceipt ?? undefined,
  });
  const printing = usePrinting();
  const [busy, setBusy] = useState(null);
  const act = (key, fn) => async () => {
    setBusy(key);
    await fn();
    setBusy(null);
  };
  return (
    <Dialog
      open
      onClose={onNext}
      title={`${t('pos.paid', 'Paid')} · ${bill.invoiceNo}`}
      description={[
        bill.changePaise > 0
          ? t('pos.returnChangeAmount', 'Return change: {{amount}}', { amount: rupees(bill.changePaise) })
          : `${t('pos.token', 'Token')} ${bill.tokenNo ?? '—'}`,
        bill.loyalty?.earned ? t('pos.pointsEarned', '+{{count}} points', { count: bill.loyalty.earned }) : null,
        bill.loyalty?.redeemed ? t('pos.pointsUsed', '{{count}} points used', { count: bill.loyalty.redeemed }) : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      size="md"
      footer={
        <div className="flex w-full flex-wrap gap-2">
          <Button
            icon={Printer}
            variant={autoPrinted ? 'secondary' : 'primary'}
            loading={busy === 'receipt'}
            onClick={act('receipt', () => printing.receipt(bill.id, { reprint: false, model: offlineReceipt }))}
          >
            {autoPrinted ? t('pos.printAgain', 'Print again') : t('pos.print', 'Print')}
          </Button>
          {kots?.length > 0 && (
            <Button variant="secondary" loading={busy === 'kot'} onClick={act('kot', () => printing.kots(kots))}>
              {t('pos.kot', 'KOT')} ({kots.length})
            </Button>
          )}
          {!offlineReceipt && (
            <>
              <a href={waLink(bill)} target="_blank" rel="noreferrer">
                <Button variant="secondary" icon={MessageCircle}>
                  WhatsApp
                </Button>
              </a>
              <EmailReceipt bill={bill} />
            </>
          )}
          <Button className="ml-auto" size="lg" onClick={onNext} data-autofocus data-testid="new-order">
            {t('pos.newOrder', 'New order')}
          </Button>
        </div>
      }
    >
      {offlineReceipt && (
        <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t(
            'pos.billedOffline',
            'Billed offline. It uploads automatically when the internet is back (the e-receipt link works after that).',
          )}
        </p>
      )}
      <div className="max-h-[55vh] overflow-y-auto rounded-lg bg-slate-100 p-3">
        {receipt.isLoading ? <Spinner /> : <ReceiptView model={receipt.data} className="shadow" />}
      </div>
    </Dialog>
  );
}
